import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import data from '../data/land-110m.json';
import {easeCubicInOut} from 'd3-ease';
import {feature} from 'topojson-client';
import {geoCircle, geoOrthographic, geoPath} from 'd3-geo';
import {interpolate} from 'd3-interpolate';
const land = feature(data, data.objects.land);
const Globe = function Globe(props) {
  const canvasRef = useRef();
  const transition = useRef();
  const renderPath = useRef();
  const centerRef = useRef(props.center);
  const style = {
    cursor: props.onClick ? 'pointer' : 'auto',
  };
  const radius = useMemo(
    function () {
      return props.diameter / 2;
    },
    [props.diameter]
  );
  const projection = useMemo(
    function () {
      return geoOrthographic()
        .scale(radius - 2)
        .translate([radius, radius])
        .rotate(
          centerRef.current.map(function (value) {
            return -value;
          })
        )
        .clipAngle(90);
    },
    [radius]
  );
  const renderGlobe = useCallback(
    function () {
      if (!canvasRef.current) {
        if (transition.current) {
          cancelAnimationFrame(transition.current);
        }
        return;
      }
      const context = canvasRef.current.getContext('2d');
      context.clearRect(
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height
      );
      context.fillStyle = props.oceanColor;
      context.beginPath();
      renderPath.current({
        type: 'Sphere',
      });
      context.fill();
      context.fillStyle = props.landColor;
      context.beginPath();
      renderPath.current(land);
      context.fill();
      const circle = geoCircle()
        .center(props.center)
        .radius(props.markerRadius)();
      context.fillStyle = props.markerColor;
      context.beginPath();
      renderPath.current(circle);
      context.fill();
    },
    [
      props.center,
      props.landColor,
      props.markerColor,
      props.markerRadius,
      props.oceanColor,
      renderPath,
    ]
  );
  useEffect(
    function () {
      if (canvasRef.current) {
        renderPath.current = geoPath()
          .projection(projection)
          .context(canvasRef.current.getContext('2d'));
        renderGlobe();
      }
    },
    [
      canvasRef,
      projection,
      props.diameter,
      props.landColor,
      props.markerColor,
      props.markerRadius,
      props.oceanColor,
      renderGlobe,
    ]
  );
  useEffect(
    function () {
      const start = Date.now();
      const duration = props.rotationTime;
      const getRotation = interpolate(
        projection.rotate(),
        props.center.map(function (value) {
          return -value;
        })
      );
      const updateRotation = function updateRotation() {
        const progress = Math.min(1, (Date.now() - start) / duration);
        projection.rotate(getRotation(easeCubicInOut(progress)));
        renderGlobe();
        if (progress < 1) {
          transition.current = requestAnimationFrame(updateRotation);
        }
      };
      if (transition.current) {
        cancelAnimationFrame(transition.current);
        transition.current = null;
      }
      transition.current = requestAnimationFrame(updateRotation);
    },
    [projection, props.center, props.rotationTime, renderGlobe]
  );
  return /*#__PURE__*/ React.createElement(
    'div',
    {
      className: 'pl-globe',
      onClick: props.onClick,
      style: style,
    },
    /*#__PURE__*/ React.createElement('canvas', {
      height: props.diameter,
      ref: canvasRef,
      width: props.diameter,
    })
  );
};
Globe.propTypes = {
  center: PropTypes.array.isRequired,
  diameter: PropTypes.number.isRequired,
  landColor: PropTypes.string.isRequired,
  markerColor: PropTypes.string.isRequired,
  markerRadius: PropTypes.number.isRequired,
  oceanColor: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  rotationTime: PropTypes.number.isRequired,
};
Globe.defaultProps = {
  center: [0, 0],
  diameter: 60,
  landColor: '#191919',
  markerColor: '#009da5',
  markerRadius: 6,
  oceanColor: '#f2f2f2',
  rotationTime: 1000,
};
export default Globe;
